import { createContext, useContext } from 'react';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type LanguageCode, type MessageKey } from './messages';

/**
 * Which language the interface speaks.
 *
 * Deliberately the *guest's* language, taken from their device — not the restaurant's `locale`. A Spanish
 * speaker at a restaurant in Texas should read Spanish. The location's locale still drives money formatting,
 * because those amounts have to match the check printed on the table: a guest comparing the screen to paper
 * should see the same "$93.85", not "93,85 US$".
 */
export function resolveLanguage(preferences: readonly string[] = navigator.languages ?? []): LanguageCode {
    for (const preference of preferences) {
        // Match on the primary subtag, so es-MX, es-419 and es all land on Spanish. Region-specific
        // catalogues are a later problem, and matching only exact tags would send es-MX to English.
        const primary = preference.toLowerCase().split('-')[0];

        const supported = SUPPORTED_LANGUAGES.find((language) => language === primary);
        if (supported) return supported;
    }

    return DEFAULT_LANGUAGE;
}

export type Translate = (key: MessageKey, values?: Record<string, string | number>) => string;

export interface Translation {
    language: LanguageCode;
    t: Translate;
}

export const TranslationContext = createContext<Translation | null>(null);

export function useTranslation(): Translation {
    const context = useContext(TranslationContext);

    if (!context) {
        throw new Error('useTranslation must be used inside a TranslationProvider.');
    }

    return context;
}
